import React from 'react';
import { TrendingUpIcon, TrendingDownIcon, DollarSignIcon, ShoppingCartIcon, UsersIcon, ClockIcon, AlertCircleIcon, CheckCircleIcon, QrCodeIcon, BarChart3Icon, ArrowRightIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
const MerchantDashboard: React.FC = () => {
  // Mock data for the dashboard
  const dashboardData = {
    summary: {
      totalSales: 125750.65,
      transactionCount: 1250,
      averageTicket: 100.6,
      activeQRCodes: 15,
      pendingDisputes: 2,
      customerCount: 875
    },
    recentTransactions: [{
      id: 'TX12345',
      amount: 1250.75,
      status: 'completed',
      customer: 'John Smith',
      time: '2023-10-28T10:30:00Z',
      method: 'QR Code'
    }, {
      id: 'TX12346',
      amount: 750.25,
      status: 'completed',
      customer: 'Sarah Johnson',
      time: '2023-10-28T09:45:00Z',
      method: 'Card Payment'
    }, {
      id: 'TX12347',
      amount: 2450.0,
      status: 'pending',
      customer: 'Robert Williams',
      time: '2023-10-28T09:15:00Z',
      method: 'QR Code'
    }, {
      id: 'TX12348',
      amount: 450.5,
      status: 'failed',
      customer: 'Emily Brown',
      time: '2023-10-28T08:30:00Z',
      method: 'Card Payment'
    }, {
      id: 'TX12349',
      amount: 1875.25,
      status: 'completed',
      customer: 'Michael Davis',
      time: '2023-10-27T16:45:00Z',
      method: 'QR Code'
    }],
    salesTrend: {
      change: 12.5,
      direction: 'up'
    },
    transactionTrend: {
      change: 8.3,
      direction: 'up'
    },
    averageTicketTrend: {
      change: 3.7,
      direction: 'up'
    },
    customerTrend: {
      change: 5.2,
      direction: 'up'
    },
    qrCodeUsage: {
      totalScans: 875,
      uniqueScans: 650,
      conversionRate: 74.3
    }
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  const formatNumber = (number: number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" data-id="element-3156">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-3157" />
            Completed
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300" data-id="element-3158">
            <ClockIcon className="w-3 h-3 mr-1" data-id="element-3159" />
            Pending
          </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" data-id="element-3160">
            <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-3161" />
            Failed
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" data-id="element-3162">
            {status}
          </span>;
    }
  };
  return <div className="space-y-6" data-id="element-3163">
      <div data-id="element-3164">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3165">
          Dashboard
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-3166">
          Overview of your merchant activity and performance.
        </p>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3167">
        <Card className="bg-white dark:bg-gray-800" data-id="element-3168">
          <div className="flex items-center justify-between" data-id="element-3169">
            <div data-id="element-3170">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3171">
                Total Sales
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3172">
                {formatCurrency(dashboardData.summary.totalSales)}
              </p>
              <div className="mt-1 flex items-center" data-id="element-3173">
                {dashboardData.salesTrend.direction === 'up' ? <TrendingUpIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-3174" /> : <TrendingDownIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-3175" />}
                <span className={`text-sm font-medium ${dashboardData.salesTrend.direction === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-3176">
                  {dashboardData.salesTrend.change}%
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30" data-id="element-3177">
              <DollarSignIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-3178" />
            </div>
          </div>
        </Card>
        <Card className="bg-white dark:bg-gray-800" data-id="element-3179">
          <div className="flex items-center justify-between" data-id="element-3180">
            <div data-id="element-3181">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3182">
                Total Transactions
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3183">
                {formatNumber(dashboardData.summary.transactionCount)}
              </p>
              <div className="mt-1 flex items-center" data-id="element-3184">
                {dashboardData.transactionTrend.direction === 'up' ? <TrendingUpIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-3185" /> : <TrendingDownIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-3186" />}
                <span className={`text-sm font-medium ${dashboardData.transactionTrend.direction === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-3187">
                  {dashboardData.transactionTrend.change}%
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/30" data-id="element-3188">
              <ShoppingCartIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" data-id="element-3189" />
            </div>
          </div>
        </Card>
        <Card className="bg-white dark:bg-gray-800" data-id="element-3190">
          <div className="flex items-center justify-between" data-id="element-3191">
            <div data-id="element-3192">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3193">
                Average Ticket
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3194">
                {formatCurrency(dashboardData.summary.averageTicket)}
              </p>
              <div className="mt-1 flex items-center" data-id="element-3195">
                {dashboardData.averageTicketTrend.direction === 'up' ? <TrendingUpIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-3196" /> : <TrendingDownIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-3197" />}
                <span className={`text-sm font-medium ${dashboardData.averageTicketTrend.direction === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-3198">
                  {dashboardData.averageTicketTrend.change}%
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30" data-id="element-3199">
              <BarChart3Icon className="h-6 w-6 text-green-600 dark:text-green-400" data-id="element-3200" />
            </div>
          </div>
        </Card>
        <Card className="bg-white dark:bg-gray-800" data-id="element-3201">
          <div className="flex items-center justify-between" data-id="element-3202">
            <div data-id="element-3203">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3204">
                Active QR Codes
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3205">
                {dashboardData.summary.activeQRCodes}
              </p>
              <div className="mt-1 flex items-center" data-id="element-3206">
                <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3207">
                  {dashboardData.qrCodeUsage.totalScans} total scans
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-indigo-100 dark:bg-indigo-900/30" data-id="element-3208">
              <QrCodeIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" data-id="element-3209" />
            </div>
          </div>
        </Card>
        <Card className="bg-white dark:bg-gray-800" data-id="element-3210">
          <div className="flex items-center justify-between" data-id="element-3211">
            <div data-id="element-3212">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3213">
                Customers
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3214">
                {formatNumber(dashboardData.summary.customerCount)}
              </p>
              <div className="mt-1 flex items-center" data-id="element-3215">
                {dashboardData.customerTrend.direction === 'up' ? <TrendingUpIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-3216" /> : <TrendingDownIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-3217" />}
                <span className={`text-sm font-medium ${dashboardData.customerTrend.direction === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-3218">
                  {dashboardData.customerTrend.change}%
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/30" data-id="element-3219">
              <UsersIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" data-id="element-3220" />
            </div>
          </div>
        </Card>
        <Card className="bg-white dark:bg-gray-800" data-id="element-3221">
          <div className="flex items-center justify-between" data-id="element-3222">
            <div data-id="element-3223">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3224">
                Pending Disputes
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3225">
                {dashboardData.summary.pendingDisputes}
              </p>
              <div className="mt-1 flex items-center" data-id="element-3226">
                <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3227">
                  Requires attention
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30" data-id="element-3228">
              <AlertCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" data-id="element-3229" />
            </div>
          </div>
        </Card>
      </div>
      {/* Recent Transactions */}
      <Card className="bg-white dark:bg-gray-800" data-id="element-3230">
        <div className="flex items-center justify-between mb-4" data-id="element-3231">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-3232">
            Recent Transactions
          </h3>
          <a href="/merchant-portal/transactions" className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 flex items-center" data-id="element-3233">
            View all
            <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-3234" />
          </a>
        </div>
        <div className="overflow-x-auto" data-id="element-3235">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3236">
            <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-3237">
              <tr data-id="element-3238">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3239">
                  Transaction ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3240">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3241">
                  Customer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3242">
                  Method
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3243">
                  Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-3244">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3245">
              {dashboardData.recentTransactions.map(transaction => <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-750" data-id="element-3246">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-3247">
                    {transaction.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-3248">
                    {formatCurrency(transaction.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3249">
                    {transaction.customer}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3250">
                    {transaction.method}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-3251">
                    {formatDate(transaction.time)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-3252">
                    {getStatusBadge(transaction.status)}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
      {/* Quick Actions */}
      <Card className="bg-white dark:bg-gray-800" data-id="element-3253">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-3254">
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3255">
          <a href="/merchant-portal/qr-generator" className="flex items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors" data-id="element-3256">
            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-800 mr-3" data-id="element-3257">
              <QrCodeIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" data-id="element-3258" />
            </div>
            <div data-id="element-3259">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3260">
                Generate QR Code
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3261">
                Create new payment QR codes
              </p>
            </div>
          </a>
          <a href="/merchant-portal/reports" className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors" data-id="element-3262">
            <div className="p-2 rounded-full bg-green-100 dark:bg-green-800 mr-3" data-id="element-3263">
              <BarChart3Icon className="h-5 w-5 text-green-600 dark:text-green-400" data-id="element-3264" />
            </div>
            <div data-id="element-3265">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3266">
                Run Reports
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3267">
                Generate custom transaction reports
              </p>
            </div>
          </a>
          <a href="/merchant-portal/campaigns" className="flex items-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors" data-id="element-3268">
            <div className="p-2 rounded-full bg-purple-100 dark:bg-purple-800 mr-3" data-id="element-3269">
              <UsersIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" data-id="element-3270" />
            </div>
            <div data-id="element-3271">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3272">
                View Campaigns
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3273">
                Check your active promotions
              </p>
            </div>
          </a>
        </div>
      </Card>
    </div>;
};
export default MerchantDashboard;